/**
 *     Import
 *     - Libraries
 */
import * as React from 'react'
import { AreaChart, Area, CartesianGrid, Tooltip } from 'recharts'

/**
 * ActivityTracker class
 *
 * @export
 * @class ActivityTracker
 * @extends {React.Component<any, any>}
 */
export default class ActivityTracker extends React.Component<any, any> {
    area: any
    tooltip: any
    point: any
    animation: boolean

    /**
     * Creates an instance of ActivityTracker.
     * @param {*} props
     * @memberof ActivityTracker
     */
    constructor(props: any) {
        super(props)

        this.area = null
        this.tooltip = null
        this.point = null
        this.animation = true

    }

    /**
     * When the component mounts, will extend the lines to the proper size
     *
     * @memberof ActivityTracker
     */
    componentDidMount() {
        this.generateFullLines()
    }

    /**
     * When the component updates, will extend the lines to the proper size
     *
     * @memberof ActivityTracker
     */
    componentDidUpdate() {
        this.generateFullLines()
    }

    /**
     * Will disable the animations if the coming data is bigger than the current one.
     * This will avoid wierd effects with the graphics
     *
     * @param {*} nextProps
     * @memberof ActivityTracker
     */
    componentWillReceiveProps(nextProps: any) {
        if (this.props.data < nextProps.data) {
            this.animation = false
        } else {
            this.animation = true
        }
    }

    /**
     * Generates longer lines
     *
     * @memberof ActivityTracker
     */
    generateFullLines = () => {
        var rectSVG = document.getElementsByClassName('recharts-cartesian-grid-vertical')[0]

        if (rectSVG) {
            for (var i = 0, childNode; i <= rectSVG.childNodes.length; i++) {
                childNode = rectSVG.childNodes[i]
                if (childNode) {
                    childNode.setAttribute('y1', '0')
                }
            }
        }
    }

    /**
     * Will return an array with the position of every line to print
     *
     * @memberof ActivityTracker
     * @param verticalGridLines number of lines to print
     * @param totalWidth the total width of the chart
     * @returns {Array} Array with the position of each line in the chart
     */
    calculateLinesPosition = (verticalGridLines, totalWidth) => {
        let arrayOfLinesPosition = new Array(verticalGridLines + 1)

        let widthDivision = totalWidth / (verticalGridLines + 1)

        for (var i = 0; i < arrayOfLinesPosition.length; i++) {
            arrayOfLinesPosition[i] = widthDivision + (arrayOfLinesPosition[i - 1] ? arrayOfLinesPosition[i - 1] : 0)
        }

        return arrayOfLinesPosition
    }

    /**
     * Will update the ToolTip when the mouse moves
     *
     * @param {*} chart
     * @memberof ActivityTracker
     */
    onChartMouseMove = (chart: any) => {
        if (chart.isTooltipActive) {
            let point = this.area.props.points[chart.activeTooltipIndex]

            if (point !== this.point) {
                this.point = point
                this.updateTooltip()
            }
        }
    }

    /**
     * Will disable ToolTip when the mouse is not in the chart
     *
     * @memberof ActivityTracker
     */
    onChartMouseLeave = () => {
        this.point = null
        this.updateTooltip()
    }

    /**
     * Will update the content and position of the ToolTip
     *
     * @memberof ActivityTracker
     */
    updateTooltip = () => {
        if (this.point) {
            let x = Math.round(this.point.x)
            let y = Math.round(this.point.y)

            this.tooltip.style.opacity = '1'
            this.tooltip.style.transform = `translate(${x}px, ${y}px)`
            /* tslint:disable:no-string-literal */
            this.tooltip.childNodes[0].childNodes[0].innerHTML = this.point.payload['date']
            this.tooltip.childNodes[0].childNodes[1].childNodes[0].innerHTML = this.point.payload['value']
            /* tslint:enable:no-string-literal */
        } else {
            this.tooltip.style.opacity = '0'
        }
    }

    render() {
        return (
            <div>
                <div className="ui-chart-tooltip" ref={ref => this.tooltip = ref}>
                    <div className="ui-chart-tooltip-content">
                        <div className="ui-chart-tooltip-content--date" />
                        <div className="ui-chart-tooltip-content--message">
                            <div className="number" />
                            <div className="text">{this.props.activityTrackerDataTab}</div>
                        </div>
                    </div>
                </div>
                <AreaChart
                    width={this.props.chartWidth}
                    height={this.props.chartHeight}
                    data={this.props.data}
                    margin={this.props.margin}
                    onMouseMove={this.onChartMouseMove}
                    onMouseLeave={this.onChartMouseLeave}
                >
                    <defs>
                        <filter
                            x="-2.2%"
                            y="-7.3%"
                            width="104.3%"
                            height="114.6%"
                            filterUnits="objectBoundingBox"
                            id="filter-3"
                        >
                            <feGaussianBlur
                                stdDeviation="15"
                                in="SourceAlpha"
                                result="shadowBlurInner1"
                            />
                            <feOffset
                                dx="0"
                                dy="20"
                                in="shadowBlurInner1"
                                result="shadowOffsetInner1"
                            />
                            <feComposite
                                in="shadowOffsetInner1"
                                in2="SourceAlpha"
                                operator="arithmetic"
                                k2="-1"
                                k3="1"
                                result="shadowInnerInner1"
                            />
                            <feColorMatrix
                                values="0 0 0 0 0.941176471   0 0 0 0 0.349019608   0 0 0 0 0.360784314  0 0 0 0.3 0"
                                type="matrix"
                                in="shadowInnerInner1"
                            />
                        </filter>
                        <linearGradient
                            id="colorUv"
                            x1="0.5"
                            y1="0"
                            x2="0.5"
                            y2="1"
                        >
                            <stop offset="1%" stopColor="#F07662" stopOpacity={1} />
                            <stop offset="100%" stopColor="#F07662" stopOpacity={0.10} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        horizontal={this.props.horizontalGrid}
                        vertical={this.props.verticalGrid}
                        style={{ opacity: 0.3 }}
                        verticalPoints={
                            this.calculateLinesPosition(this.props.verticalGridLines, this.props.chartWidth)
                        }
                        horizontalPoints={
                            this.calculateLinesPosition(this.props.horizontalGridLines, this.props.chartWidth)
                        }
                    />
                    <Tooltip />

                    <Area
                        type="monotone"
                        dataKey="value"
                        fillOpacity={1}
                        fill="#fff"
                        isAnimationActive={this.animation}
                    />
                    <Area
                        ref={ref => this.area = ref}
                        type="monotone"
                        dataKey="value"
                        stroke="#"
                        fillOpacity={1}
                        fill="url(#colorUv)"
                        isAnimationActive={this.animation}
                    />
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#"
                        fillOpacity={1}
                        filter="url(#filter-3)"
                        isAnimationActive={this.animation}
                    />
                </AreaChart>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/pages/Dashboard/components/ActivityTracker.tsx